// Appointment service for sending emails and saving to Google Sheets

// E-posta ve Google Sheets ayarlari notificationService'te toplandi;
// siparisler de ayni yeri kullaniyor.
import { sendOwnerEmail, appendToSheet } from './notificationService';

interface AppointmentData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  appointmentDate: string;
  appointmentTime: string;
  serviceType: string;
  message: string;
}


// Randevu e-postasi (ortak bildirim servisi uzerinden)
export async function sendAppointmentEmail(appointmentData: AppointmentData): Promise<boolean> {
  return sendOwnerEmail({
    from_name: `${appointmentData.firstName} ${appointmentData.lastName}`,
    from_email: appointmentData.email,
    phone: appointmentData.phone,
    appointment_date: appointmentData.appointmentDate,
    appointment_time: appointmentData.appointmentTime,
    service_type: appointmentData.serviceType,
    message: appointmentData.message,
  });
}

// Randevuyu Google Sheets tablosuna ekle (ortak bildirim servisi uzerinden)
export async function saveToGoogleSheets(appointmentData: AppointmentData): Promise<boolean> {
  const result = await appendToSheet('appointment', {
    firstName: appointmentData.firstName,
    lastName: appointmentData.lastName,
    email: appointmentData.email,
    phone: appointmentData.phone,
    appointmentDate: appointmentData.appointmentDate,
    appointmentTime: appointmentData.appointmentTime,
    serviceType: appointmentData.serviceType,
    message: appointmentData.message,
  });
  return result.ok;
}

/* 
NOT: Burada JSON anahtar dosyanız var ama güvenlik nedeniyle kullanılmamalı.
JSON key'leri frontend'de kullanmak güvenli değildir çünkü herkese açık olur.

İKİ YÖNTEM VAR:

1. GOOGLE APPS SCRIPT (ÖNERİLEN - DAHA GÜVENLİ):
   - APPOINTMENT_SETUP.md dosyasındaki talimatları izleyin
   - Google Apps Script'i kullanın (önerilen yöntem)
   - Bu yöntem JSON key'i kullanmaz, sadece webhook gibi çalışır

2. BACKEND İLE JSON KEY KULLANIMI:
   - Backend (Node.js/Express) ekleyin
   - JSON key'i sadece backend'de kullanın
   - Frontend backend'e istek atsın, backend Google Sheets API'yi çağırsın
   
Şu anda bu React uygulaması (frontend) olduğu için Apps Script yöntemini kullanıyoruz.
Eğer backend eklemek isterseniz, o zaman JSON key'i güvenle backend'de kullanabilirsiniz.
*/

// Combined function to send email and save to sheets
export async function submitAppointment(appointmentData: AppointmentData): Promise<{
  emailSent: boolean;
  sheetSaved: boolean;
}> {
  const emailSent = await sendAppointmentEmail(appointmentData);
  const sheetSaved = await saveToGoogleSheets(appointmentData);
  
  return { emailSent, sheetSaved };
}

// Bir randevu saatinin dolu olup olmadigini gosteren kimlik.
// Sabit uretildigi icin ayni saat icin her zaman ayni dokumana denk gelir.
export function slotId(date: string, time: string): string {
  return `${date}_${time}`.replace(/[^0-9A-Za-z:_-]/g, '');
}

// Musaitlik kontrolu.
//
// GUVENLIK: Eskiden bu kontrol 'appointments' koleksiyonunu okuyordu; o
// koleksiyonda ad, soyad, telefon ve e-posta var ve kural herkese okuma izni
// veriyordu. Yani siteye giren herkes tum randevu sahiplerinin iletisim
// bilgilerini cekebiliyordu. Artik sadece tarih ve saat iceren, PII barindirmayan
// 'bookedSlots' koleksiyonu okunuyor.
export async function checkAppointmentAvailability(appointmentDate: string, appointmentTime: string): Promise<{ available: boolean; reason?: string }> {
  try {
    // Gecmis tarih kontrolu. Formdaki min ozelligi istemci tarafinda
    // degistirilebildigi icin burada da dogruluyoruz.
    if (appointmentDate < todayISO()) {
      return { available: false, reason: 'Geçmiş bir tarihe randevu alınamaz.' };
    }

    const { doc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const slotRef = doc(db, 'bookedSlots', slotId(appointmentDate, appointmentTime));
    const slotSnap = await getDoc(slotRef);

    if (slotSnap.exists()) {
      return { available: false, reason: 'Bu saat dilimi dolu.' };
    }

    const disabledSlotsRef = collection(db, 'disabledTimeSlots');
    const disabledQuery = query(
      disabledSlotsRef,
      where('date', '==', appointmentDate),
      where('time', '==', appointmentTime),
      where('active', '==', true)
    );

    const disabledSnapshot = await getDocs(disabledQuery);

    if (!disabledSnapshot.empty) {
      return { available: false, reason: 'Bu saat kapalı.' };
    }

    return { available: true };
  } catch (error) {
    console.error('Error checking appointment availability:', error);
    return { available: false, reason: 'Kontrol edilemedi.' };
  }
}

// Randevuyu kaydet.
//
// Once saat rezerve edilir, sonra randevu yazilir. Rezervasyon dokumaninin
// kimligi tarih+saatten uretildigi ve guvenlik kurallari yalnizca "create"e
// izin verdigi icin, ayni saate ikinci bir yazma denemesi Firestore tarafindan
// reddedilir. Bu, iki kisinin ayni anda ayni saati almasini engeller -
// eski "once oku sonra yaz" yonteminde bu yaris acikti.
export async function saveAppointmentToFirestore(appointmentData: AppointmentData): Promise<boolean> {
  try {
    const availability = await checkAppointmentAvailability(
      appointmentData.appointmentDate,
      appointmentData.appointmentTime
    );

    if (!availability.available) {
      alert(availability.reason || 'Bu saat müsait değil.');
      return false;
    }

    const { addDoc, collection, doc, setDoc, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    // 1) Saati rezerve et (PII icermez, herkes okuyabilir)
    try {
      await setDoc(
        doc(db, 'bookedSlots', slotId(appointmentData.appointmentDate, appointmentData.appointmentTime)),
        {
          date: appointmentData.appointmentDate,
          time: appointmentData.appointmentTime,
          createdAt: Timestamp.fromDate(new Date()),
        }
      );
    } catch (reserveError) {
      console.error('Slot rezerve edilemedi:', reserveError);
      alert('Bu saat az önce başkası tarafından alındı. Lütfen başka bir saat seçin.');
      return false;
    }

    // 2) Randevunun kendisi (PII icerir, sadece yonetim okuyabilir)
    await addDoc(collection(db, 'appointments'), {
      ...appointmentData,
      createdAt: Timestamp.fromDate(new Date()),
      status: 'pending'
    });

    return true;
  } catch (error) {
    console.error('Error saving appointment to Firestore:', error);
    return false;
  }
}


// --- Randevu durumlari ---
//
// pending    : Talep alindi, henuz onaylanmadi
// confirmed  : Onaylandi, musteri bekleniyor
// arrived    : Musteri geldi (gecmise tasinir)
// no_show    : Musteri gelmedi (gecmise tasinir)
// cancelled  : Iptal edildi (gecmise tasinir, saat yeniden acilir)
export type AppointmentStatus = 'pending' | 'confirmed' | 'arrived' | 'no_show' | 'cancelled';

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  pending: 'Bekleniyor',
  confirmed: 'Onaylandı',
  arrived: 'Geldi',
  no_show: 'Gelmedi',
  cancelled: 'İptal Edildi',
};

// Bu durumlardaki randevular "gecmis" sayilir
export const CLOSED_APPOINTMENT_STATUSES: AppointmentStatus[] = ['arrived', 'no_show', 'cancelled'];

// Bugunu YYYY-MM-DD olarak dondurur (yerel saate gore).
// toISOString() UTC'ye cevirdigi icin Turkiye'de aksam saatlerinde
// bir sonraki gunu verebiliyor; bu yuzden elle bicimlendiriyoruz.
export function todayISO(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Randevu gecmise mi ait? (durumu kapali VEYA tarihi gecmis)
export function isPastAppointment(appointment: { status?: string; appointmentDate?: string }): boolean {
  if (appointment.status && CLOSED_APPOINTMENT_STATUSES.includes(appointment.status as AppointmentStatus)) {
    return true;
  }
  if (appointment.appointmentDate && appointment.appointmentDate < todayISO()) {
    return true;
  }
  return false;
}

// Randevu durumunu degistir.
// Iptal edilirse rezerve edilen saat yeniden acilir ki baskasi alabilsin.
export async function updateAppointmentStatus(
  appointmentId: string,
  newStatus: AppointmentStatus,
  appointment?: { appointmentDate?: string; appointmentTime?: string }
): Promise<boolean> {
  try {
    const { doc, updateDoc, deleteDoc, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    await updateDoc(doc(db, 'appointments', appointmentId), {
      status: newStatus,
      updatedAt: Timestamp.fromDate(new Date()),
    });

    if (newStatus === 'cancelled' && appointment?.appointmentDate && appointment?.appointmentTime) {
      try {
        await deleteDoc(
          doc(db, 'bookedSlots', slotId(appointment.appointmentDate, appointment.appointmentTime))
        );
      } catch (slotError) {
        // Saat serbest birakilamadiysa randevu yine de iptal edilmis sayilir
        console.error('Iptal edilen randevunun saati serbest birakilamadi:', slotError);
      }
    }

    return true;
  } catch (error) {
    console.error('Randevu durumu guncellenemedi:', error);
    return false;
  }
}

// Admin functions
export async function getAppointments(): Promise<any[]> {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const appointmentsRef = collection(db, 'appointments');
    const querySnapshot = await getDocs(appointmentsRef);
    
    const appointments: any[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    }));
    
    // Sort by date and time in JavaScript
    appointments.sort((a: any, b: any) => {
      if (a.appointmentDate !== b.appointmentDate) {
        return b.appointmentDate.localeCompare(a.appointmentDate);
      }
      return b.appointmentTime.localeCompare(a.appointmentTime);
    });
    
    return appointments;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    return [];
  }
}

export async function disableTimeSlot(date: string, time: string, reason: string = ''): Promise<boolean> {
  try {
    const { addDoc, collection, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    await addDoc(collection(db, 'disabledTimeSlots'), {
      date,
      time,
      reason,
      active: true,
      createdAt: Timestamp.fromDate(new Date())
    });

    return true;
  } catch (error) {
    console.error('Error disabling time slot:', error);
    return false;
  }
}

export async function enableTimeSlot(date: string, time: string): Promise<boolean> {
  try {
    const { collection, query, where, getDocs, updateDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const disabledRef = collection(db, 'disabledTimeSlots');
    const q = query(
      disabledRef,
      where('date', '==', date),
      where('time', '==', time),
      where('active', '==', true)
    );

    const querySnapshot = await getDocs(q);
    
    const promises = querySnapshot.docs.map(doc => 
      updateDoc(doc.ref, { active: false })
    );

    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('Error enabling time slot:', error);
    return false;
  }
}

export async function getDisabledTimeSlots(): Promise<any[]> {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const disabledRef = collection(db, 'disabledTimeSlots');
    const q = query(
      disabledRef,
      where('active', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const slots: any[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    }));
    
    // Sort by date and time in JavaScript
    slots.sort((a: any, b: any) => {
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      return b.time.localeCompare(a.time);
    });
    
    return slots;
  } catch (error) {
    console.error('Error fetching disabled time slots:', error);
    return [];
  }
}

const appointmentService = {
  sendAppointmentEmail,
  saveToGoogleSheets,
  submitAppointment,
  saveAppointmentToFirestore,
  checkAppointmentAvailability,
  getAppointments,
  disableTimeSlot,
  enableTimeSlot,
  getDisabledTimeSlots,
  updateAppointmentStatus,
  isPastAppointment,
  todayISO
};

export default appointmentService;

